<script setup lang="ts">
import { RouterView } from 'vue-router'
import { theme } from 'ant-design-vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import enUS from 'ant-design-vue/es/locale/en_US'
import { computed, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import axios from 'axios'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/en'
import { designTokens } from './theme/tokens'
import { useThemeStore } from './stores/theme'
import { useLangStore } from './stores/lang'
import { useSiteStore } from './stores/site'
import { useModalHeightAnimation } from './composables/useModalHeightAnimation'
import { usePageTitle } from './composables/usePageTitle'

const themeStore = useThemeStore()
const langStore = useLangStore()
const siteStore = useSiteStore()

// Load the site name/description once so pages can replace the default branding
siteStore.ensureLoaded()

// Keep the browser window title in sync with the current route and language
usePageTitle()

// Smooth out height changes on every dialog (tab switches, expanding content)
useModalHeightAnimation()

const antdLocale = computed(() => (langStore.lang === 'en' ? enUS : zhCN))

// 语言切换时同步 dayjs 与 <html lang>，保证日期组件 / 时间格式跟随界面语言
function applyLocale() {
  dayjs.locale(langStore.lang === 'en' ? 'en' : 'zh-cn')
  document.documentElement.lang = langStore.lang === 'en' ? 'en' : 'zh-CN'
  // 未经过 api/client 的原始 axios 请求也要携带界面语言，供后端返回本地化文案
  axios.defaults.headers.common['ui-lang'] = langStore.lang === 'en' ? 'en' : 'zh'
}

onMounted(() => {
  themeStore.init()
  applyLocale()
})

watch(() => langStore.lang, applyLocale)

// 主题切换时同步 AntDesign 的暗色算法与基础背景色
const antdTheme = computed(() => ({
  algorithm: themeStore.isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: {
    colorPrimary: designTokens.color.primary,
    colorSuccess: designTokens.color.success,
    colorWarning: designTokens.color.warning,
    colorError: designTokens.color.error,
    colorBgBase: themeStore.isDark ? designTokens.color.surfaceDark : designTokens.color.surface,
  },
}))
</script>

<template>
  <a-config-provider :locale="antdLocale" :theme="antdTheme">
    <RouterView />
  </a-config-provider>
</template>
